import { CapacitorHttp } from '@capacitor/core';
import { isCapacitorNative } from './capacitor-platform';
import { needsNativeDiscoveryFetch } from '../../oidc-discovery.loader';
import { LoggerService } from '../utils/logger.service';

/**
 * POST JSON using Capacitor native HTTP when the API host differs from the WebView origin (avoids CORS / status 0).
 */
export async function postJsonNative<T>(
  url: string,
  body: unknown,
  headers: Record<string, string> = {}
): Promise<T> {
  if (!isCapacitorNative() && !needsNativeDiscoveryFetch(url)) {
    throw new Error('postJsonNative called for same-origin URL');
  }
  LoggerService.log('[native-http] POST', { url });
  const response = await CapacitorHttp.post({
    url,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...headers },
    data: body,
    responseType: 'json',
  });
  if (response.status < 200 || response.status >= 300) {
    const err = response.data ?? { error: `HTTP ${response.status}` };
    throw { status: response.status, error: err };
  }
  const data = response.data;
  if (data && typeof data === 'object') {
    return data as T;
  }
  if (typeof data === 'string') {
    return JSON.parse(data) as T;
  }
  throw new Error('Response was not JSON');
}

/** GET JSON using Capacitor native HTTP (same CORS bypass as postJsonNative). */
export async function getJsonNative<T>(
  url: string,
  headers: Record<string, string> = {}
): Promise<T> {
  if (!isCapacitorNative() && !needsNativeDiscoveryFetch(url)) {
    throw new Error('getJsonNative called for same-origin URL');
  }
  LoggerService.log('[native-http] GET', { url });
  const response = await CapacitorHttp.get({
    url,
    headers: { Accept: 'application/json', ...headers },
    responseType: 'json',
  });
  if (response.status < 200 || response.status >= 300) {
    const err = response.data ?? { error: `HTTP ${response.status}` };
    throw { status: response.status, error: err };
  }
  const data = response.data;
  if (data && typeof data === 'object') {
    return data as T;
  }
  if (typeof data === 'string') {
    return JSON.parse(data) as T;
  }
  throw new Error('Response was not JSON');
}

/** GET binary body via Capacitor native HTTP (pack downloads). */
export async function getBinaryNative(
  url: string,
  headers: Record<string, string> = {}
): Promise<{ body: ArrayBuffer; responseHeaders: Record<string, string> }> {
  if (!isCapacitorNative() && !needsNativeDiscoveryFetch(url)) {
    throw new Error('getBinaryNative called for same-origin URL');
  }
  LoggerService.log('[native-http] GET binary', { url });
  const response = await CapacitorHttp.get({
    url,
    headers: { ...headers },
    responseType: 'arraybuffer',
  });
  if (response.status < 200 || response.status >= 300) {
    throw { status: response.status, error: response.data ?? `HTTP ${response.status}` };
  }
  const raw = response.data;
  let body: ArrayBuffer;
  if (raw instanceof ArrayBuffer) {
    body = raw;
  } else if (typeof raw === 'string') {
    const binary = atob(raw.replace(/\s+/g, ''));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    body = bytes.buffer;
  } else {
    throw new Error('Binary response has unexpected type');
  }
  const responseHeaders: Record<string, string> = {};
  const hdrs = response.headers ?? {};
  for (const [k, v] of Object.entries(hdrs)) {
    if (typeof v === 'string') {
      responseHeaders[k.toLowerCase()] = v;
    }
  }
  return { body, responseHeaders };
}
